import type { ParsedUrlQuery } from 'querystring';
import type { Data } from '@puckeditor/core';
import { useQuery } from '@apollo/client';
import { useRouter } from 'next/compat/router';

import { CmsContentVersionDocument } from 'src/gql/graphql';
import { isPuckData } from './puck-utils';

export const CMS_PREVIEW_MODEL_ID_QUERY_PARAM = 'cmsModelId';
export const CMS_PREVIEW_VERSION_ID_QUERY_PARAM = 'cmsVersionId';

/**
 * Extracts the optional CMS version preview ID from query params, ONLY if:
 * 1. Both `cmsModelId` and `cmsVersionId` query params are present as non-empty scalar strings.
 * 2. `cmsModelId` exactly matches `expectedModelId`.
 *
 * If either param is missing, repeated/array-valued, empty, or if `cmsModelId`
 * does not match `expectedModelId`, returns `undefined`.
 */
export function getMatchingPreviewVersionId(
  query: ParsedUrlQuery | undefined,
  expectedModelId: string
): string | undefined {
  if (!query) return undefined;

  const rawModelId = query[CMS_PREVIEW_MODEL_ID_QUERY_PARAM];
  const rawVersionId = query[CMS_PREVIEW_VERSION_ID_QUERY_PARAM];

  if (typeof rawModelId !== 'string' || typeof rawVersionId !== 'string') {
    return undefined;
  }

  if (!rawModelId || !rawVersionId || rawModelId.trim() !== rawModelId || rawVersionId.trim() !== rawVersionId) {
    return undefined;
  }

  if (rawModelId !== expectedModelId) {
    return undefined;
  }

  return rawVersionId;
}

/** Reads the active preview version ID for a given model from the Next.js router query. */
export function useCmsPreviewVersionId(expectedModelId: string): string | undefined {
  const router = useRouter();
  return getMatchingPreviewVersionId(router?.query, expectedModelId);
}

type CmsVersionPreview = {
  data: Data | undefined;
  isPreviewing: boolean;
  loading: boolean;
};

/**
 * Fetches preview data only when both URL parameters are valid for this renderer's model. The
 * version's `data` is untyped JSON on the wire (same as the resolve-by-targeting read) -- run it
 * through `isPuckData` here so both fetch paths converge on the one shared `Data | undefined`
 * shape `useCmsResolvedContent` returns, regardless of which source served it.
 */
export function useCmsVersionPreview(expectedModelId: string, enterpriseId: string | undefined): CmsVersionPreview {
  const versionId = useCmsPreviewVersionId(expectedModelId);
  const isPreviewing = Boolean(versionId);
  const { data, loading } = useQuery(CmsContentVersionDocument, {
    variables: { enterpriseId: enterpriseId ?? '', versionId: versionId ?? '' },
    skip: !enterpriseId || !versionId,
  });

  const versionData = data?.cmsContentVersion?.data;

  return {
    data: isPuckData(versionData) ? versionData : undefined,
    isPreviewing,
    loading,
  };
}
